import SlideFrame from "./_SlideFrame";
import { fmt, PRICING } from "@/lib/api";

const retained = [
  { label: "Active current account", price: fmt(PRICING.current_per_month), unit: "per account / month", note: "AI-enhanced servicing + call center included" },
  { label: "Active past-due account", price: `+${fmt(PRICING.past_due_per_month)}`, unit: "additional / month", note: "$13/mo total when an account is past-due — collections workflows + recovery" },
  { label: "Account set-up", price: fmt(PRICING.setup_per_account), unit: "per account, one-time", note: "Boarding, validation, first-cycle setup" },
  { label: "Monthly minimum", price: fmt(PRICING.monthly_minimum), unit: "per month", note: "Applies only if usage is below the minimum" },
  { label: "Onboarding & integration", price: fmt(PRICING.onboarding_one_time), unit: "one-time · with minimum commitment", note: "API integration, data migration, playbook configuration" },
];

const shared = [
  { label: "Convenience fee", price: "$2.75", unit: "per transaction · 50 / 50 split", note: "Optional borrower-elected — helps offset payment-processor cost" },
  { label: "NSF fee", price: "$12.00", unit: "per occurrence · 50 / 50 split", note: "Helps offset NSF cost the lender pays the processor" },
  { label: "Late fee", price: "$10.00", unit: "per occurrence · 50 / 50 split", note: "On past-due payments per program rules" },
];

export default function SlidePricing() {
  return (
    <SlideFrame tag="04 · Economics">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <p className="overline mb-6">Flat. Transparent. Predictable.</p>
          <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium">
            A <em className="italic text-primary">flat-dollar</em> model — not a tax on your UPB.
          </h2>
          <p className="mt-8 text-foreground/75 leading-relaxed max-w-md">
            Traditional servicers charge a percentage of Unpaid Principal Balance. As your portfolio
            grows, so does your bill — even if the work stays the same. We don&rsquo;t. You pay for
            accounts, not balances.
          </p>
          <p className="mt-6 text-sm font-mono-data text-muted-foreground">
            See your savings on the next slide →
          </p>
        </div>

        <div className="lg:col-span-7">
          <p className="overline mb-3">What IBA retains</p>
          <div className="border-t border-foreground">
            {retained.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-4 py-5 border-b border-foreground/20 items-baseline">
                <span className="col-span-12 md:col-span-5 font-display text-xl md:text-2xl">{r.label}</span>
                <span className="col-span-6 md:col-span-3 font-mono-data text-2xl md:text-3xl text-primary">{r.price}</span>
                <span className="col-span-6 md:col-span-4 text-[11px] uppercase tracking-brand text-muted-foreground">
                  {r.unit}
                </span>
                <span className="col-span-12 text-sm text-muted-foreground -mt-2">{r.note}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-white border-t-4 border-t-primary border-x border-b border-foreground/20 p-6">
            <p className="overline mb-2">Compliance bundle</p>
            <div className="flex items-baseline gap-4">
              <span className="font-mono-data text-3xl text-foreground">{fmt(PRICING.compliance_monthly)}</span>
              <span className="text-xs uppercase tracking-brand text-muted-foreground">per month, flat</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Entire team and enterprise solution — full compliance staff, compliance management system,
              portfolio insights, and complaint management — via our CompliSun partnership.
            </p>
          </div>
        </div>
      </div>

      {/* Shared revenue (50/50) section */}
      <div className="mt-12 border-t border-foreground pt-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Shared 50 / 50</p>
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter leading-[1] font-medium">
              On every dollar of ancillary revenue, you keep <em className="italic text-primary">half</em>.
            </h3>
            <p className="mt-5 text-foreground/75 leading-relaxed max-w-md">
              Most servicers capture every fee they earn on your portfolio and keep it. We don&rsquo;t.
              Convenience, NSF, and late fees we collect while servicing your book — split 50/50.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="border-t border-foreground">
              {shared.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 py-5 border-b border-foreground/20 items-baseline">
                  <span className="col-span-12 md:col-span-5 font-display text-xl md:text-2xl">{r.label}</span>
                  <span className="col-span-6 md:col-span-3 font-mono-data text-2xl md:text-3xl text-primary">{r.price}</span>
                  <span className="col-span-6 md:col-span-4 text-[11px] uppercase tracking-brand text-muted-foreground">
                    {r.unit}
                  </span>
                  <span className="col-span-12 text-sm text-muted-foreground -mt-2">{r.note}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-foreground/70 leading-relaxed">
              <span className="overline mr-2">Cost responsibility</span>
              The lender pays payment-processor fees and NSF cost paid to the processor — the
              shared convenience and NSF fees above are designed to offset that.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-foreground pt-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="overline mb-4">Not discount servicing — efficient servicing</p>
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter leading-[1] font-medium">
              A price this low is only credible from a team that&rsquo;s <em className="italic text-primary">already done it</em> at capital-markets standard.
            </h3>
          </div>
          <div className="lg:col-span-8 space-y-4">
            <p className="text-base text-foreground/80 leading-relaxed max-w-2xl">
              $3/loan/month doesn&rsquo;t buy discount servicing. After the 50/50 revenue share on
              ancillary fees, effective servicing cost typically lands closer to <span className="text-primary font-semibold">$1.75/loan/month</span>.
              Our servicing leads have spent years building consumer-servicing programs at
              institutional scale — with default outcomes below published industry benchmarks.
            </p>
            <p className="text-[11px] uppercase tracking-brand text-muted-foreground font-mono-data">
              See Operator Track Record — slide 15
            </p>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}
